/**
 * `RemoteController<S, Cmd>` — the **shared-mode** optimistic command-room client,
 * factored once. It is the frontend analogue of the backend's `CommandRoom`: where
 * the room is the server-authoritative half (apply → broadcast → reconcile), this
 * is the client half — apply a command **optimistically** to a local history for
 * instant UI, send the raw command to the authoritative server, then reconcile with
 * the server's `*_change` broadcast (last-write-wins).
 *
 * The two rooms differ only in (a) the wire-message vocabulary, (b) which history
 * they seed, (c) the encounter's explicit per-room `encounter_join` handshake, and
 * (d) the roster's local `compute(...)` mixin. Everything else — dispatch/undo/redo/
 * onMessage/handle/publish/subscribe, the optimistic-apply-then-reconcile loop — is
 * identical and lives here once. The per-domain bits stay as **overrides/mixins,
 * never flattened into the base**:
 *   - `RemoteEncounterController` overrides {@link RemoteController.sendJoin} to
 *     emit the per-room handshake, and {@link RemoteController.onWelcome} to record
 *     the joined room id.
 *   - `RemoteRosterController` adds `computePilot`/`computeAll` (a roster-only
 *     concern — encounter state carries no derived view) as its own methods.
 *
 * The flow, once:
 *   - **boot:** the transport wiring constructs the controller and (for the
 *     deferred-join encounter room) calls {@link sendJoin} on socket-open. On the
 *     server's welcome, {@link onWelcome} seeds a fresh local history from the
 *     authoritative raw state and adopts the assigned actor id.
 *   - **dispatch(cmd):** apply optimistically to the local history (instant UI),
 *     enqueue the command onto the unacked queue under its `seq`, then send the raw
 *     command (stamped with that same `seq`). A local reducer rejection is surfaced
 *     immediately and NOT sent (the server would reject identically).
 *   - **reconcile:** on every `*_change`, set {@link authoritative} to the server's
 *     state, drop every queued command the echoed `watermark` covers (`seq <=
 *     watermark` — the server has processed those), then **rebase**: rebuild the
 *     optimistic view by replaying the remaining unacked queue, in order, on top of
 *     `authoritative`. A replayed command that no longer applies cleanly is dropped
 *     silently (a dev-only debug log, never a user-facing error) — the same posture
 *     the "stale inverse rejected as a no-op" undo rule encodes. This stops the
 *     reconcile from clobbering a client's own still-in-flight optimistic work (a
 *     visible rewind at real latency); LWW remains the conflict rule *between* clients.
 *   - **undo/redo:** sent to the server (`*_undo`/`*_redo`); the server owns the
 *     authoritative per-actor stacks and broadcasts the result. We do NOT
 *     optimistically undo — undo correctness depends on the server's stacks (and
 *     stale-inverse rejection), so the round-trip drives it. **Gated on an empty
 *     unacked queue:** undo/redo are server intents that must not reason about
 *     unacked edits rebasing over an undone state, so a call while the queue is
 *     non-empty is *deferred* (held, not sent) and flushed automatically the next
 *     time a reconcile drains the queue to empty.
 *
 * Persistence: none — a shared room is the server's state; the client is a live
 * view. Transport is injected as a minimal {@link RemoteConnection} so this is
 * unit-testable against a fake and wired to a real `WebSocket` in the hooks.
 */
import {
  encode,
  decodeMessage,
  ok,
  err,
  type CommandError,
  type Result,
  type Participant,
  type UndoStateMessage,
  type RosterClientMessage,
  type EncounterClientMessage,
  type RosterServerMessage,
  type EncounterServerMessage,
  type RosterWelcomeMessage,
  type EncounterWelcomeMessage,
  type RosterChangeMessage,
  type EncounterChangeMessage,
  type RosterCommandRejectedMessage,
  type EncounterCommandRejectedMessage,
  errorMessage,
} from "@lancer-vtt/shared";

/**
 * An inbound server message the controller dispatches on. It is the union of the
 * two command rooms' server messages — the generic switches on `.type` (compared
 * against the per-domain {@link RemoteProtocol}) and narrows the payload itself.
 */
export type RemoteServerMessage = RosterServerMessage | EncounterServerMessage;
import type { UndoState } from "@lancer-vtt/domain";

/**
 * The minimal transport+identity surface a controller needs (a real `WebSocket`
 * wrapper satisfies it). A *connection* carries both transport (`send`/`close`) AND
 * the **identity credential** — the `/ws/game` reclaim `token` the room verifies in
 * the hello to bind the socket to a slot. Bundling the token here (rather than as a
 * separate controller arg) keeps the controller constructor at the 3-param convention.
 */
export type RemoteConnection = {
  send(data: string): void;
  close(): void;
  /** The reclaim token presented in the room hello. */
  token: string;
}

/**
 * The result of an optimistic local dispatch — the unified {@link Result},
 * specialised to a value-less success. `ok(undefined)` means the command applied;
 * `err(message)` carries a reducer rejection's message for the UI. The remote and
 * solo (`rosterController`) paths share this one definition.
 */
export type DispatchResult = Result<void>;

/**
 * The minimal per-actor command history surface the controller drives — the
 * subset of `History<S, Cmd, Event>` it touches. `RosterHistory`/`EncounterHistory`
 * both satisfy it (they extend the `History` generic).
 */
export type RemoteHistory<S, Cmd> = {
  /** The authoritative raw state — read for snapshots, reassigned on reconcile. */
  state: S;
  /**
   * Apply a command optimistically for one actor. Returns a {@link Result}:
   * `ok(events)` on success, `err(commandError)` on an expected rejection — the
   * events are opaque here (the controller only branches on `ok`).
   */
  applyCommand(command: Cmd, actorId: string): Result<unknown, CommandError>;
}

/**
 * The wire-message vocabulary for one room. The four message-type strings the
 * controller emits, plus the inbound `*_welcome`/`*_change` type strings it
 * reconciles on. The room differences are ENTIRELY captured here — the LWW loop
 * itself never branches on domain.
 */
export type RemoteProtocol = {
  /** `"roster_command"` / `"encounter_command"`. */
  command: string;
  /** `"roster_undo"` / `"encounter_undo"`. */
  undo: string;
  /** `"roster_redo"` / `"encounter_redo"`. */
  redo: string;
  /** `"roster_welcome"` / `"encounter_welcome"` — seeds the history. */
  welcome: string;
  /** `"roster_change"` / `"encounter_change"` — the LWW reconcile broadcast. */
  change: string;
  /**
   * `"roster_command_rejected"` / `"encounter_command_rejected"` — sent to the
   * originating socket only, carrying the advanced `watermark`. A rejected command
   * produces no `*_change` broadcast, so this is the sole signal that reaches the
   * unacked queue for a LONE rejection with no other traffic — {@link
   * RemoteController.handle}'s reject branch drives the same
   * {@link RemoteController.rebase} the change branch does.
   */
  rejected: string;
}

/** A reconciled read of the authoritative state (the snapshot React reads). */
export type RemoteSnapshot<S> = {
  state: S;
}

const EMPTY_UNDO: UndoState = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

/**
 * One sent-but-not-yet-acknowledged command on the unacked queue — the `seq` it was
 * stamped with, the raw command, and the actor it was applied/sent under (so a rebase
 * replay re-applies it exactly as `dispatch` originally did).
 */
type UnackedEntry<Cmd> = {
  readonly seq: number;
  readonly command: Cmd;
  readonly actorId: string;
};

/**
 * Dev/test-only debug log (mirrors `presentationDirector.ts`'s `devWarn`):
 * `import.meta.env.PROD` is statically `true` in a shipped Vite build, so this call
 * is dead-code-eliminated from production; it fires in dev and under the test
 * runner. Used ONLY for the "a rebased command no longer applies cleanly, dropped
 * silently" case — that must never surface as a user-facing error.
 */
function devDebug(message: string): void {
  if (import.meta.env?.PROD !== true) console.debug(message);
}

export type RemoteControllerArgs<S, Cmd> = {
  conn: RemoteConnection;
  protocol: RemoteProtocol;
  initial: RemoteHistory<S, Cmd>;
  reseed: (state: S) => RemoteHistory<S, Cmd>;
};

export abstract class RemoteController<S, Cmd> {
  protected readonly conn: RemoteConnection;
  private readonly protocol: RemoteProtocol;
  /**
   * The reclaim token presented in the room hello — read off the connection
   * (transport + identity). The server verifies it and binds this socket to the
   * verified slot id; **the controller never asserts an `actorId` on the wire**,
   * learning its verified id back from `*_welcome.you`.
   */
  protected get token(): string {
    return this.conn.token;
  }

  /** Local optimistic history, seeded from `*_welcome`, rebuilt (rebased) on reconcile. */
  protected history: RemoteHistory<S, Cmd>;
  private snapshot: RemoteSnapshot<S>;
  private readonly listeners = new Set<() => void>();

  /**
   * The last server-reconciled state — seeded from `*_welcome`, updated on every
   * `*_change`. The base for {@link rebase}'s replay, and the encounter subclass's
   * `*_command_rejected` snap-back target. `protected` so a subclass reads it directly.
   */
  protected authoritative: S;

  /**
   * The ordered queue of commands sent but not yet acknowledged by the server —
   * pushed in {@link dispatch} under the `seq` it was sent with, drained in
   * {@link rebase} (drop `seq <= watermark`, replay the remainder). Cleared on
   * `*_welcome` — a fresh connection means a fresh server-side socket, so a stale
   * queued command must never replay onto it.
   */
  private unacked: UnackedEntry<Cmd>[] = [];

  /**
   * At most one deferred `undo`/`redo` intent, held while {@link unacked} is
   * non-empty — see {@link undo}/{@link redo}. A second call before the queue drains
   * replaces the held intent (last-one-wins). Cleared on `*_welcome` alongside
   * {@link unacked}.
   */
  private pendingUndoRedo: { kind: "undo" | "redo"; scope?: string } | null = null;

  /** Our server-assigned identity (from `*_welcome`); null until welcomed. */
  protected you: Participant | null = null;
  /**
   * The latest server-reported undo availability, keyed by **scope**. The default scope
   * `""` is the only key the encounter/game/presence rooms ever push (they never stamp a
   * scope). A roster window reads its own `mech:`/`pilot:` scope; a scope not yet pushed
   * defaults to {@link EMPTY_UNDO} (a freshly-opened MECH.app shows no stale availability
   * until that mech's first edit).
   */
  private readonly undoByScope = new Map<string, UndoState>();
  /** True once `*_welcome` has seeded us. */
  private ready = false;

  /**
   * Per-connection monotonic send counter — stamped on every sent command as `seq`,
   * and the key {@link unacked} entries are pushed/dropped under. The server tracks +
   * echoes a per-socket watermark off it, which {@link rebase} consumes on every
   * reconcile. Not reset on reconnect/reseed (a fresh connection gets a fresh socket
   * server-side too, so restarting from 1 here would only ever collide with a
   * watermark of 0, never a stale one) — but {@link unacked} IS cleared on welcome,
   * so a fresh session never replays a queued command from the old one.
   */
  private seq = 0;

  /**
   * This socket's own last-echoed watermark — mirrored here from every
   * `*_change`/`*_rejected` message's `watermark` field as {@link handle} processes it,
   * purely so a caller can read the CURRENT value between messages ({@link watermark}'s
   * getter) without threading its own tracking. `BrowserMatchHandle` awaits
   * `watermark >= (the seq {@link dispatch} just stamped)` to correlate a COMMAND
   * submission's own server-side settlement — `encounter_intent` deliberately carries
   * no `seq` (see that message's own doc), so this getter is meaningless for an intent;
   * `BrowserMatchHandle` correlates those a different way (see its own header).
   */
  private watermark_ = 0;

  /**
   * The seq span of the most recent **multi-command gesture** ({@link dispatchGesture}), plus
   * whether a rejection inside it has already been surfaced — or `null` when no gesture is open.
   *
   * A gesture is *one user action* that costs more than one command on the wire (today: the
   * pre-activation `activate_unit` + `boost` pair, `projects/pre-activation-affordances.md` D6).
   * The server settles each command separately, so a lost race rejects BOTH — and the second
   * rejection's reason is a *consequence* of the first, not a cause. See
   * {@link coalesceRejection} for what the window is for; it is closed by
   * {@link closeSettledGesture} as soon as the watermark covers its last command.
   */
  private gesture_: { first: number; last: number; rejected: boolean } | null = null;

  /** Builds a fresh history from the authoritative raw state in `*_welcome`. */
  private readonly reseed: (state: S) => RemoteHistory<S, Cmd>;

  /**
   * @param args.conn      injected transport (real `WebSocket` or a fake).
   * @param args.protocol  the room's wire-message vocabulary.
   * @param args.initial   the history to start from before the welcome seeds the real one.
   * @param args.reseed    builds a fresh history from the authoritative raw state in `*_welcome`.
   */
  constructor(args: RemoteControllerArgs<S, Cmd>) {
    const { conn, protocol, initial, reseed } = args;
    this.conn = conn;
    this.protocol = protocol;
    this.reseed = reseed;
    this.history = initial;
    this.authoritative = initial.state;
    this.snapshot = { state: initial.state };
  }

  // ── Handshake (the hello-gated bind) ─────────────────────────────────────────

  /**
   * Send the room's opening hello, presenting the reclaim token so the server binds
   * this socket to the verified slot id. The roster room sends `roster_hello { token }`
   * (the base default); the per-encounter room overrides this to fold the token into
   * `encounter_join`. The transport wiring calls this on socket-open. Both rooms are
   * hello-gated — there is no implicit join.
   */
  sendJoin(): void {
    this.conn.send(encode({ type: "roster_hello", token: this.token } as unknown as RosterClientMessage));
  }

  // ── Read surface ─────────────────────────────────────────────────────────────

  getSnapshot(): RemoteSnapshot<S> {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Our server-assigned actor id, or null until the welcome arrives. */
  get actorId(): string | null {
    return this.you?.id ?? null;
  }

  /** True once the welcome handshake has seeded authoritative state. */
  get connected(): boolean {
    return this.ready;
  }

  /**
   * The `seq` {@link dispatch} stamped on the MOST RECENT command it sent (0 before any
   * command has ever been sent this session) — a caller that wants to await that exact
   * command's server-side settlement reads this immediately after `dispatch()` returns
   * (synchronous — `dispatch` stamps `seq` before returning), then polls/awaits
   * {@link watermark} reaching it. See {@link watermark_}'s doc for the motivating caller.
   */
  get lastSeq(): number {
    return this.seq;
  }

  /**
   * This socket's own last-echoed watermark (see {@link watermark_}'s doc). Advances
   * whenever a `*_change` or `*_rejected` message lands, mirroring the server's own
   * per-socket "highest seq processed" counter — 0 before this socket has ever sent a
   * seq-carrying command.
   */
  get watermark(): number {
    return this.watermark_;
  }

  /**
   * This actor's server-reported undo/redo availability for `scope` (default `""`,
   * the only scope the non-roster rooms use). A scope not yet pushed reads as
   * {@link EMPTY_UNDO}.
   */
  undoState(scope = ""): UndoState {
    return this.undoByScope.get(scope) ?? EMPTY_UNDO;
  }

  // ── Write surface ────────────────────────────────────────────────────────────

  /**
   * Optimistically apply `command` locally, enqueue it onto the unacked queue, and
   * send it to the authoritative server. `actorId` defaults to our own
   * server-assigned id. A local reducer rejection is surfaced immediately (the UI
   * mirrors the rules, so this is rare) and NOT sent, NOT enqueued; the optimistic
   * state stays as-is. A server-side rejection of a command that *did* apply locally
   * is corrected by the next reconcile's rebase.
   */
  dispatch(command: Cmd, actorId?: string): DispatchResult {
    const who = actorId ?? this.you?.id;
    if (who === undefined) return err(this.notConnectedError());
    // The reducer apply is wrapped so a genuine bug thrown out of it cannot die
    // silently inside the player's own click handler before `send` ever runs.
    // `threw` distinguishes the two DIFFERENT outcomes below: a caught throw is a
    // bug and fails OPEN (still sent); the `!result.ok` branch is an EXPECTED
    // reducer rejection and stays unsent. **Keep these two paths visibly distinct** —
    // never fold a throw into the rejection arm or vice versa.
    let threw = false;
    try {
      const result = this.history.applyCommand(command, who);
      if (!result.ok) {
        // An expected reducer rejection — surfaced for the UI, not sent; the
        // optimistic state stays as-is.
        return err(result.error.message);
      }
    } catch (error) {
      // A genuine bug in the reducer, not an expected rejection. Left uncaught
      // this throws inside the player's click handler and the command never
      // reaches `send` below, with nothing logged on either end. Report it, then
      // fail OPEN: send the command anyway. That is safe *by construction* —
      // `commandHistory.ts` only assigns `this.state`/the undo stack inside
      // `result.ok`, so a throw here leaves `this.history` completely untouched;
      // there is no partial local apply to unwind. The authoritative broadcast
      // reconciles moments later, same as any other command.
      threw = true;
      console.error(
        `RemoteController.dispatch: optimistic apply threw for command "${String((command as { type?: unknown }).type)}" — sending anyway (fail-open)`,
        error,
      );
    }
    // Reached on a clean success OR a caught throw (fail-open) — never on the
    // `!result.ok` rejection above, which already returned. Skip `publish()`
    // when we threw: the reducer never got past the throw, so no local state
    // changed and there is nothing to republish (publishing would be harmless,
    // not wrong — skipping is just cleaner).
    if (!threw) this.publish();
    // Identity is server-authoritative: **no `actorId` on the wire** — the server
    // attributes to the socket's bound slot. We still apply optimistically under our own
    // verified id (`who`, from `*_welcome.you`) so the local view matches.
    // Capture `seq` ONCE — it both keys the unacked queue entry and stamps the wire
    // message, so the two never drift apart. Every SENT command — including a fail-open
    // one — belongs on the unacked queue, or the watermark/undo-defer machinery drifts
    // from what the wire actually carries.
    const seq = ++this.seq;
    this.unacked.push({ seq, command, actorId: who });
    this.send({ type: this.protocol.command, command, seq });
    return ok(undefined);
  }

  /**
   * Dispatch `commands` as **one user gesture**: N ordinary {@link dispatch} calls, in order,
   * stopping at the first LOCAL reducer rejection (which is surfaced and not sent, exactly as a
   * lone `dispatch` would). The seq span is recorded so the rejections the server may relay for
   * them can be coalesced into the one that names the real cause ({@link coalesceRejection}).
   *
   * **Ordinary commands, never a `batch`.** This is the whole point of the method existing rather
   * than callers building a `batch` command: the encounter room routes by leaf `command.type`, and
   * a `batch` is not a member of `HOOKED_COMMAND_TYPE_RECORD`
   * (`packages/rules/src/encounter/referee/dispatchClassification.ts`), so it falls through to a
   * plain apply and **silently skips every child's hook chain**. See
   * {@link withLeadingActivation} for the worked case (`activate_unit` + `boost`) and the tests
   * that go red if anyone collapses it.
   *
   * **A mid-gesture local rejection leaves the earlier commands SENT**, and that is correct rather
   * than a gap: the server settles each of them individually too, so a client-side all-or-nothing
   * pre-flight would only diverge from what the room actually does. For today's one gesture it
   * cannot arise — a fresh `activate_unit` mints a full budget and carries no attack lock, so the
   * trailing `boost` cannot be locally refused once the lead applied.
   */
  dispatchGesture(commands: Cmd[], actorId?: string): DispatchResult {
    if (commands.length === 0) return ok(undefined);
    // A one-command "gesture" is just a dispatch — and it must CLEAR any stale window rather than
    // open one, or an ordinary lone rejection could be swallowed as a follow-on of an older pair.
    if (commands.length === 1) {
      this.gesture_ = null;
      return this.dispatch(commands[0]!, actorId);
    }
    const first = this.seq + 1;
    this.gesture_ = null;
    for (const command of commands) {
      const result = this.dispatch(command, actorId);
      if (!result.ok) {
        // Whatever DID go out still belongs to one gesture (the server may reject it), so record
        // the span we actually sent before returning the local rejection.
        if (this.seq >= first) this.gesture_ = { first, last: this.seq, rejected: false };
        return result;
      }
    }
    this.gesture_ = { first, last: this.seq, rejected: false };
    return ok(undefined);
  }

  /**
   * **Gesture-scoped rejection coalescing** (`projects/pre-activation-affordances.md` D6/3.2).
   * Returns `true` when a rejection stamped `watermark` is a **follow-on** inside the open gesture
   * — i.e. the gesture's real cause has already been surfaced — and the caller should therefore
   * leave the surfaced rejection alone.
   *
   * The case it exists for: two ready units race for the same activation. The loser's
   * `activate_unit` is rejected server-side (*"X is mid-turn; end its turn before activating Y"* —
   * the real cause), and its trailing `boost` is rejected too (*"boost: combatant Y is not the
   * active actor"* — a restatement of the consequence). A single latest-wins rejection slot shows
   * the player the second one. First-in-gesture wins instead.
   *
   * Outside an open gesture this always returns `false`, so an ordinary lone rejection — and a
   * later, unrelated one — still surfaces and still clobbers exactly as before.
   */
  protected coalesceRejection(watermark: number): boolean {
    const g = this.gesture_;
    if (g === null || watermark < g.first || watermark > g.last) return false;
    if (g.rejected) return true;
    g.rejected = true;
    return false;
  }

  /**
   * Close the gesture window once the watermark covers its last command — every command in it has
   * been settled (applied or rejected), so nothing further can belong to it. Called from
   * {@link handle} after the watermark has been advanced by a `*_change`/`*_rejected`.
   */
  private closeSettledGesture(): void {
    if (this.gesture_ !== null && this.watermark_ >= this.gesture_.last) this.gesture_ = null;
  }

  /**
   * Ask the server to undo the last command in `scope` (authoritative stacks live
   * server-side; attributed to the bound slot). `scope` is the undo sub-partition: a
   * roster window passes its own `mech:`/`pilot:` scope so the undo only reverses that
   * subject's edits; the encounter/game rooms pass none and the server walks the single
   * default-scope stack (an undefined `scope` is dropped by `encode`, so their wire
   * bytes are unchanged).
   *
   * **Gated on an empty unacked queue**: undo is a server intent, not a local optimistic
   * command, and it must not reason about unacked edits rebasing over an undone state.
   * While {@link unacked} is non-empty this DEFERS the intent (held in
   * {@link pendingUndoRedo}, NOT sent) rather than sending it now; the next reconcile
   * that drains the queue to empty flushes it automatically. The queue is near-always
   * empty, so the wait is effectively invisible.
   */
  undo(scope?: string): boolean {
    if (this.you === null) return false;
    if (this.unacked.length > 0) {
      this.pendingUndoRedo = { kind: "undo", scope };
      return true;
    }
    this.send({ type: this.protocol.undo, scope });
    return true;
  }

  /**
   * Ask the server to redo the last undone command in `scope` (attributed to the
   * bound slot). Mirror of {@link undo}, including the empty-queue gate/defer.
   */
  redo(scope?: string): boolean {
    if (this.you === null) return false;
    if (this.unacked.length > 0) {
      this.pendingUndoRedo = { kind: "redo", scope };
      return true;
    }
    this.send({ type: this.protocol.redo, scope });
    return true;
  }

  // ── Transport ────────────────────────────────────────────────────────────────

  /**
   * Feed a raw server message string in (the ws `onmessage` handler calls this).
   *
   * A frame that isn't valid JSON is dropped silently — the server only ever sends
   * `encode()`d frames, so a malformed one means the transport is already broken and
   * the close handler owns it. Read through `decodeMessage` — the shared wire decode on the
   * `Result` channel — rather than a hand-rolled `try`/`catch` around `decode` (a bare
   * `JSON.parse` + cast): `CODE_STYLE.md`'s `Result` section is explicit that the throwing
   * stdlib API gets wrapped once, at the boundary, never at the call site. This is the same
   * decode-or-drop every `backend/` and `cli/` ws handler runs.
   */
  onMessage(raw: string): void {
    const decoded = decodeMessage<RemoteServerMessage>(raw);
    if (!decoded.ok) return; // decode-or-drop
    this.handle(decoded.value);
  }

  /** Handle a decoded server message (split out so tests can drive it directly). */
  handle(msg: RemoteServerMessage): void {
    // `protocol.*` are runtime strings (not literal types), so narrow by cast.
    if (msg.type === this.protocol.welcome) {
      const w = msg as RosterWelcomeMessage | EncounterWelcomeMessage;
      this.you = w.you;
      // Seed the local history from the authoritative raw state. Opaque on the
      // wire; it IS the room's state (the server's `History.state`).
      this.authoritative = w.state as S;
      // A fresh welcome means a fresh connection + fresh server-side socket: any
      // queued unacked command or deferred undo/redo intent belongs to the OLD
      // session and must not replay onto this one.
      this.unacked = [];
      this.pendingUndoRedo = null;
      // …and so does any open gesture window: its seqs belong to the old socket, and the
      // watermark reset below would otherwise leave it open forever.
      this.gesture_ = null;
      // A fresh connection is a fresh server-side socket, so its watermark starts at 0
      // too (mirroring `unacked`'s own reset just above) — a stale non-zero value here
      // would make `lastSeq`/`watermark`-based awaits (see {@link watermark_}'s doc)
      // resolve immediately on the new connection's very first command.
      this.watermark_ = 0;
      this.history = this.reseed(w.state as S);
      this.ready = true;
      this.onWelcome(msg);
      this.publish();
      return;
    }
    if (msg.type === this.protocol.change) {
      // Reconcile: adopt the server's state as authoritative, drop every queued
      // command the echoed watermark covers, then rebuild the optimistic view by
      // replaying whatever remains on top of it — this is what stops the reconcile
      // from clobbering our own still-in-flight optimistic work (a visible rewind at
      // real latency). LWW stays the conflict rule *between* clients.
      const c = msg as RosterChangeMessage | EncounterChangeMessage;
      this.authoritative = c.state as S;
      this.watermark_ = c.watermark;
      this.rebase(c.watermark);
      this.closeSettledGesture();
      this.publish();
      this.flushPendingUndoRedo();
      return;
    }
    if (msg.type === this.protocol.rejected) {
      // Watermark-on-reject: a rejected command produces no `*_change` broadcast,
      // so this is the ONLY signal that ever advances the queue for a lone
      // rejection with zero subsequent traffic. Reuse the exact same rebase the
      // change branch does —
      // `authoritative` did not move (no state changed), but the watermark did,
      // so `rebase` drops the rejected entry (its `seq <= watermark`) and replays
      // whatever legitimately remains, closing the phantom optimistic apply and
      // un-wedging any deferred undo/redo.
      const r = msg as RosterCommandRejectedMessage | EncounterCommandRejectedMessage;
      this.watermark_ = r.watermark;
      this.rebase(r.watermark);
      this.closeSettledGesture();
      this.publish();
      this.flushPendingUndoRedo();
      return;
    }
    if (msg.type === "undo_state") {
      const u = msg as UndoStateMessage;
      // Key by the affected scope the server stamped. The encounter/game/presence rooms
      // never stamp one, so `u.scope ?? ""` keeps a single default-scope entry for them.
      this.undoByScope.set(u.scope ?? "", {
        canUndo: u.canUndo,
        canRedo: u.canRedo,
        undoLabel: u.undoLabel,
        redoLabel: u.redoLabel,
      });
      // Republish a fresh snapshot, not a bare notify: the React adapter
      // (`useSyncExternalStore`) only re-renders when `getSnapshot()` returns a new
      // reference, and undo-state lives outside that snapshot object. A bare `notify`
      // fires the listener but the unchanged snapshot ref makes React bail, so the
      // undo/redo buttons never reflect a `undo_state`-only update (the case after a
      // command: the room sends `*_change` then a *separate* `undo_state`). Publishing
      // swaps the snapshot ref so the new undo availability actually reaches the DOM.
      this.publish();
      return;
    }
    // `pong` and any unrecognized message: ignore.
  }

  /** Tear down the connection. */
  close(): void {
    this.conn.close();
  }

  // ── Extension seams (overridden by per-domain subclasses) ────────────────────

  /**
   * Post-welcome hook, run after the base has seeded `you`/history and flipped
   * `ready`, before the first publish. Default no-op; the encounter controller
   * overrides it to record the joined room id from `encounter_welcome`.
   */
  protected onWelcome(_msg: RemoteServerMessage): void {}

  /** The error returned by `dispatch` before the welcome handshake connects us. */
  protected abstract notConnectedError(): string;

  // ── Internals ────────────────────────────────────────────────────────────────

  /**
   * Encode + send a room message. The `type` is a runtime `string` here (from the
   * per-domain `RemoteProtocol`); it is always one of the room's client-message
   * variants, so the cast to the wire union is sound — `encode` is `JSON.stringify`.
   */
  private send(msg: { type: string } & Record<string, unknown>): void {
    this.conn.send(encode(msg as unknown as RosterClientMessage | EncounterClientMessage));
  }

  /**
   * The reconcile core: drop every unacked entry the server has processed
   * (`seq <= watermark`), then rebuild `this.history` from {@link authoritative} by
   * replaying whatever remains, in order, under the actor id it was originally applied
   * under. A replayed command that no longer applies cleanly is dropped **silently** —
   * a dev-only debug log, never a user-facing error, mirroring the "stale inverse
   * rejected as a no-op, not rebased" undo posture. Does NOT publish — callers publish
   * once, after this returns.
   */
  private rebase(watermark: number): void {
    const surviving = this.unacked.filter((u) => u.seq > watermark);
    const next = this.reseed(this.authoritative);
    const kept: UnackedEntry<Cmd>[] = [];
    for (const u of surviving) {
      // This runs inside the websocket message handler. Left uncaught, a throw here
      // would abort the loop before `this.unacked`/`this.history` are reassigned
      // below and before the caller's `publish()` ever runs — the client silently
      // freezes on stale state, looking like a disconnect, with nothing logged.
      // Treat a throw EXACTLY like the `!result.ok` arm right below: this replayed
      // command no longer applies cleanly, drop it, keep going.
      let result: ReturnType<typeof next.applyCommand>;
      try {
        result = next.applyCommand(u.command, u.actorId);
      } catch (error) {
        devDebug(
          `RemoteController: dropped unacked command (seq ${u.seq}) whose rebase replay threw: ${errorMessage(error)}`,
        );
        continue;
      }
      if (result.ok) {
        kept.push(u);
      } else {
        devDebug(
          `RemoteController: dropped unacked command (seq ${u.seq}) that no longer applies cleanly on rebase: ${result.error.message}`,
        );
      }
    }
    this.unacked = kept;
    this.history = next;
  }

  /**
   * Send a held {@link pendingUndoRedo} intent once {@link unacked} has drained to
   * empty — called after every reconcile. A no-op when the queue is still non-empty
   * or nothing is held.
   */
  private flushPendingUndoRedo(): void {
    if (this.unacked.length > 0 || this.pendingUndoRedo === null) return;
    const { kind, scope } = this.pendingUndoRedo;
    this.pendingUndoRedo = null;
    this.send({ type: kind === "undo" ? this.protocol.undo : this.protocol.redo, scope });
  }

  protected publish(): void {
    this.snapshot = { state: this.history.state };
    this.notify();
  }

  protected notify(): void {
    for (const l of this.listeners) l();
  }
}
